import { avisosFirmados, catalogo, referenciaDe, type EstadoZona, type Foto } from '@taller/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Ban,
  ClipboardCheck,
  Download,
  Ellipsis,
  FileText,
  Mail,
  Phone,
  SendHorizontal,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  useDispositivos,
  useInspeccion,
  usePerfil,
  useUsuarios,
  useVerificacionFotos,
} from '@/datos/consultas';
import { aFecha, fechaInspeccion, type InspeccionDoc, type VerificacionFoto } from '@/datos/tipos';
import { ComparadorDeslizante } from '@/evidencia/Comparador';
import { Galeria } from '@/evidencia/Galeria';
import { InsigniaHash } from '@/evidencia/EstadoHash';
import { descargar, estadoHash } from '@/evidencia/urls';
import { Visor, type ItemVisor } from '@/evidencia/Visor';
import { conteoDanos, Gemelo } from '@/gemelo/Gemelo';
import { Avisos, sinComparacion } from '@/inspecciones/Avisos';
import { DialogoAnular, DialogoRevision } from '@/inspecciones/dialogos';
import { DialogoEnviarPdf } from '@/inspecciones/EnviarPdf';
import { nombreTipo } from '@/inspecciones/filtros';
import { InsigniaEstado } from '@/inspecciones/InsigniaEstado';
import { Accesorios, Combustible, Firma, ListaZonas } from '@/inspecciones/partes';
import { permisos, useSesionActiva } from '@/lib/sesion';
import { fechaCompleta, fechaHora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto, Tarjeta, TituloTarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';
import { Menu, MenuContenido, MenuDisparador, MenuItem, MenuSeparador } from '@/ui/menu';
import { formatearPatente, Placa } from '@/ui/patente';
import { ChipSeveridad } from '@/ui/severidad';

export const Route = createFileRoute('/_app/inspecciones/$inspeccionId')({
  component: Detalle,
});

const ordenZonas = catalogo.zonas.map((z) => z.id);
const ordenarFotos = (fs: Foto[]) =>
  [...fs].sort(
    (a, b) =>
      ordenZonas.indexOf(a.zona) - ordenZonas.indexOf(b.zona) ||
      a.capturadaEn.localeCompare(b.capturadaEn),
  );

function Detalle() {
  const { inspeccionId } = Route.useParams();
  const q = useInspeccion(inspeccionId);
  if (q.isError) {
    return (
      <>
        <Encabezado titulo="Inspección" volver={{ a: '/inspecciones' }} />
        <div className="p-6">
          <ErrorCarga error={q.error} />
        </div>
      </>
    );
  }
  if (q.isPending) return <Cargando />;
  if (!q.data) {
    return (
      <>
        <Encabezado titulo="Inspección" volver={{ a: '/inspecciones' }} />
        <Vacio
          icono={<FileText />}
          titulo="No encontramos esta inspección"
          accion={
            <Boton asChild>
              <Link to="/inspecciones">Volver al listado</Link>
            </Boton>
          }
        >
          Puede que el enlace esté incompleto o que pertenezca a otro taller.
        </Vacio>
      </>
    );
  }
  return <Contenido i={q.data} />;
}

function Contenido({ i }: { i: InspeccionDoc }) {
  const s = useSesionActiva();
  const perfil = usePerfil(s.uid);
  const usuarios = useUsuarios();
  // Device names are admin-only (rules): other roles see the id.
  const dispositivos = useDispositivos(permisos.administrar(s.rol));
  const verifsQ = useVerificacionFotos(i.id);
  // Reference it is compared against: the Ingreso of a reingreso/entrega, or
  // the siniestro an Ingreso started from (shared referenciaDe).
  const referencia = referenciaDe(i);
  const recepcion = useInspeccion(referencia?.id ?? null);
  const recepcionVerifs = useVerificacionFotos(referencia?.id ?? null);
  const siniestro = i.tipo === 'siniestro';
  const [zona, setZona] = useState<string | null>(null);
  const [visor, setVisor] = useState<{ items: ItemVisor[]; i: number } | null>(null);
  const [revisar, setRevisar] = useState(false);
  const [anular, setAnular] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [enviar, setEnviar] = useState(false);

  const nombreYo = perfil.data?.nombre ?? s.nombre;
  const verifs = useMemo(() => new Map((verifsQ.data ?? []).map((v) => [v.id, v])), [verifsQ.data]);
  const fotos = useMemo(() => ordenarFotos(Object.values(i.fotos ?? {})), [i.fotos]);
  const visibles = zona ? fotos.filter((f) => f.zona === zona) : fotos;
  const estadosHash = fotos.map((f) => estadoHash(verifs.get(f.id), f));
  const malas = estadosHash.filter((e) => e === 'alterada' || e === 'no_declarada').length;
  const pendientes = estadosHash.filter((e) => e === 'pendiente').length;
  const estados = Object.fromEntries(Object.entries(i.zonas ?? {}).map(([k, v]) => [k, v.estado]));
  const conteo = conteoDanos(estados);
  const autor =
    usuarios.data?.find((u) => u.id === i.creadaPor)?.nombre ?? i.creadaPorNombre ?? 'Recepción';
  const fecha = fechaInspeccion(i);
  const creada = aFecha(i.creadaEn);
  const revisadaEn = aFecha(i.revision?.en);
  const firmada = i.estado === 'firmada';
  // Sending is logged in `avisos`, which only admin and jefe de taller write.
  const puedeEnviar = firmada && !!i.pdfPath && permisos.revisar(s.rol);
  const titulo =
    i.estado === 'borrador'
      ? `${nombreTipo[i.tipo]} en borrador`
      : `${nombreTipo[i.tipo]} N° ${i.numeroCorrelativo ?? '—'}`;

  const abrir = (f: Foto, lista: Foto[], vs: Map<string, VerificacionFoto>) =>
    setVisor({ items: lista.map((x) => ({ foto: x, verif: vs.get(x.id) })), i: lista.indexOf(f) });

  const bajarPdf = async () => {
    if (!i.pdfPath) return;
    setBajando(true);
    try {
      const nombre = siniestro ? 'informe-siniestro' : i.tipo === 'recepcion' ? 'ingreso' : i.tipo;
      await descargar(i.pdfPath, `${nombre}-${i.numeroCorrelativo ?? i.id}-${i.patente}.pdf`);
    } catch {
      toast.error('No se pudo descargar el PDF', {
        description: 'Revisa tu conexión e inténtalo de nuevo.',
      });
    } finally {
      setBajando(false);
    }
  };

  const acciones = (
    <>
      {i.pdfPath ? (
        <Boton onClick={bajarPdf} cargando={bajando} className="max-sm:hidden">
          <Download /> PDF
        </Boton>
      ) : firmada ? (
        <span className="hidden text-[13px] text-text-2 md:inline">Generando PDF…</span>
      ) : null}
      {puedeEnviar && (
        <Boton onClick={() => setEnviar(true)} className="max-sm:hidden">
          <SendHorizontal /> Enviar al cliente
        </Boton>
      )}
      {firmada && permisos.revisar(s.rol) && (
        <Boton
          variante={i.revision ? 'secundario' : 'primario'}
          onClick={() => setRevisar(true)}
          className="max-sm:hidden"
        >
          <ClipboardCheck /> {i.revision ? 'Cambiar revisión' : 'Revisar'}
        </Boton>
      )}
      <Menu>
        <MenuDisparador asChild>
          <Boton tamano="icono" aria-label="Más acciones">
            <Ellipsis />
          </Boton>
        </MenuDisparador>
        <MenuContenido className="w-60">
          <MenuItem icono={<FileText />} asChild>
            <Link to="/vehiculos/$patente" params={{ patente: i.patente }}>
              Ficha del vehículo
            </Link>
          </MenuItem>
          {i.pdfPath && (
            <MenuItem icono={<Download />} onSelect={() => void bajarPdf()} className="sm:hidden">
              Descargar PDF
            </MenuItem>
          )}
          {puedeEnviar && (
            <MenuItem
              icono={<SendHorizontal />}
              onSelect={() => setEnviar(true)}
              className="sm:hidden"
            >
              Enviar al cliente
            </MenuItem>
          )}
          {firmada && permisos.revisar(s.rol) && (
            <MenuItem
              icono={<ClipboardCheck />}
              onSelect={() => setRevisar(true)}
              className="sm:hidden"
            >
              Revisar
            </MenuItem>
          )}
          {firmada && permisos.anular(s.rol) && (
            <>
              <MenuSeparador />
              <MenuItem icono={<Ban />} peligro onSelect={() => setAnular(true)}>
                Anular inspección…
              </MenuItem>
            </>
          )}
        </MenuContenido>
      </Menu>
    </>
  );

  return (
    <>
      <Encabezado
        ayuda="inspecciones"
        titulo={titulo}
        subtitulo={`${formatearPatente(i.patente)} · ${i.vehiculo.marca} ${i.vehiculo.modelo}${i.vehiculo.anio ? ` ${i.vehiculo.anio}` : ''} · ${fecha ? fechaHora(fecha) : ''}`}
        volver={{ a: '/inspecciones', texto: 'Volver a inspecciones' }}
        acciones={acciones}
      />

      <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-5 px-4 py-5 sm:px-7 sm:py-6">
        <Avisos i={i} malas={malas} />

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="flex min-w-0 flex-col gap-5">
            <Tarjeta className="p-5 sm:p-6">
              <TituloTarjeta accion={<InsigniaEstado inspeccion={i} />}>Carrocería</TituloTarjeta>
              <div className="mt-4 flex flex-col gap-6 sm:flex-row">
                <div className="flex flex-col items-center gap-2 sm:w-[190px]">
                  <Gemelo
                    estados={estados}
                    ancho={170}
                    seleccionada={zona}
                    onSeleccionar={setZona}
                    etiqueta="Diagrama de daños: toca una zona para ver sus fotos"
                  />
                  <p className="text-center text-[12px] text-text-2">
                    Toca una zona para filtrar las fotos
                  </p>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                  <div className="flex flex-wrap gap-1.5">
                    {(['que', 'gol', 'ray'] as EstadoZona[]).map((e) =>
                      conteo[e] ? <ChipSeveridad key={e} estado={e} cantidad={conteo[e]} /> : null,
                    )}
                    {!conteo.que && !conteo.gol && !conteo.ray && <ChipSeveridad estado="sin" />}
                  </div>
                  <ListaZonas zonas={i.zonas ?? {}} seleccionada={zona} onSeleccionar={setZona} />
                </div>
              </div>
            </Tarjeta>

            <Tarjeta className="p-5 sm:p-6">
              <TituloTarjeta
                accion={
                  zona ? (
                    <button
                      type="button"
                      onClick={() => setZona(null)}
                      className="text-[13px] font-bold text-primary-text hover:underline"
                    >
                      Ver todas
                    </button>
                  ) : undefined
                }
              >
                Evidencia
                <span className="tabular ml-2 text-[13px] font-normal text-text-2">
                  {zona
                    ? `${visibles.length} de ${fotos.length} · ${catalogo.zonas.find((z) => z.id === zona)?.nombre}`
                    : `${fotos.length} ${fotos.length === 1 ? 'foto' : 'fotos'}`}
                  {fotos.length > 0 && !zona && (
                    <>
                      {' · '}
                      {malas > 0 ? (
                        <b className="text-que-texto">{malas} con problemas</b>
                      ) : pendientes > 0 ? (
                        `${pendientes} por verificar`
                      ) : (
                        'todas verificadas'
                      )}
                    </>
                  )}
                </span>
              </TituloTarjeta>
              <div className="mt-4">
                <Galeria
                  fotos={visibles}
                  verifs={verifs}
                  onAbrir={(f) => abrir(f, visibles, verifs)}
                />
              </div>
            </Tarjeta>

            {referencia && (
              <Comparacion
                i={i}
                tipoReferencia={referencia.tipo}
                recepcion={recepcion.data ?? null}
                cargando={recepcion.isPending}
                verifs={verifs}
                verifsRecepcion={new Map((recepcionVerifs.data ?? []).map((v) => [v.id, v]))}
                onAbrir={abrir}
              />
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-5">
            <Tarjeta className="p-5">
              <div className="flex items-center gap-4">
                <Placa patente={i.patente} tamano="lg" />
                <div className="min-w-0">
                  <p className="truncate text-[17px] font-bold">
                    {i.vehiculo.marca} {i.vehiculo.modelo} {i.vehiculo.anio ?? ''}
                  </p>
                  <p className="text-[14px] text-text-2">{i.vehiculo.color}</p>
                </div>
              </div>
              {!siniestro && (
                <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-[14px]">
                  <Dato t="Kilometraje">
                    {i.vehiculo.km !== undefined
                      ? `${i.vehiculo.km.toLocaleString('es-CL')} km`
                      : '—'}
                  </Dato>
                  <Dato t="Combustible">
                    <Combustible nivel={i.combustible} />
                  </Dato>
                  <Dato t="Motivo">
                    {catalogo.motivosIngreso.find((m) => m.id === i.motivo)?.nombre ?? i.motivo}
                  </Dato>
                  {i.aseguradora && <Dato t="Aseguradora">{i.aseguradora}</Dato>}
                  {i.nroSiniestro && (
                    <Dato t="N° siniestro">
                      <span className="tabular">{i.nroSiniestro}</span>
                    </Dato>
                  )}
                </dl>
              )}
              <Link
                to="/vehiculos/$patente"
                params={{ patente: i.patente }}
                className="mt-4 inline-flex text-[14px] font-bold text-primary-text hover:underline"
              >
                Ver ficha e historial del vehículo →
              </Link>
            </Tarjeta>

            <Tarjeta className="p-5">
              <TituloTarjeta>Cliente</TituloTarjeta>
              <p className="mt-2 text-[16px] font-bold">{i.cliente.nombre}</p>
              <p className="tabular text-[14px] text-text-2">{i.cliente.rut}</p>
              <div className="mt-3 flex flex-col gap-1.5 text-[14px]">
                {i.cliente.telefono && (
                  <a
                    href={`tel:${i.cliente.telefono.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-2 text-primary-text hover:underline"
                  >
                    <Phone className="size-4" /> {i.cliente.telefono}
                  </a>
                )}
                {i.cliente.correo && (
                  <a
                    href={`mailto:${i.cliente.correo}`}
                    className="inline-flex items-center gap-2 break-all text-primary-text hover:underline"
                  >
                    <Mail className="size-4" /> {i.cliente.correo}
                  </a>
                )}
              </div>
            </Tarjeta>

            {siniestro && <Seguro i={i} />}

            {!siniestro && (
              <Tarjeta className="p-5">
                <TituloTarjeta>Accesorios</TituloTarjeta>
                <div className="mt-3">
                  <Accesorios valores={i.accesorios ?? {}} />
                </div>
                {(i.objetosValor || i.observaciones) && (
                  <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 text-[14px]">
                    {i.objetosValor && (
                      <div>
                        <p className="text-[12.5px] font-bold text-text-2">
                          Objetos de valor declarados
                        </p>
                        <p>{i.objetosValor}</p>
                      </div>
                    )}
                    {i.observaciones && (
                      <div>
                        <p className="text-[12.5px] font-bold text-text-2">Observaciones</p>
                        <p className="text-text-long">{i.observaciones}</p>
                      </div>
                    )}
                  </div>
                )}
              </Tarjeta>
            )}

            <Custodia
              total={fotos.length}
              verificadas={estadosHash.filter((e) => e === 'verificada').length}
              pendientes={pendientes}
              malas={malas}
              i={i}
              bajando={bajando}
              onPdf={() => void bajarPdf()}
            />

            <Tarjeta className="p-5">
              <TituloTarjeta>Firma y registro</TituloTarjeta>
              <div className="mt-3 flex flex-col gap-4">
                {i.firma && <Firma firma={i.firma} titulo="Firma del cliente" />}
                {i.firmaRetiro && <Firma firma={i.firmaRetiro} titulo="Firma de retiro" />}
                {!i.firma && !i.firmaRetiro && (
                  <p className="text-[14px] text-text-2">
                    {siniestro
                      ? i.estado === 'borrador'
                        ? 'Aún sin cerrar. La firma del cliente es opcional en el siniestro.'
                        : 'Sin firma del cliente (es opcional en el siniestro).'
                      : 'Aún sin firma del cliente.'}
                  </p>
                )}
                {!siniestro && (
                  <p className="text-[13px] text-text-2">
                    {i.avisosAceptados
                      ? '✓ El cliente aceptó el estado registrado y los avisos legales.'
                      : 'El cliente aún no acepta los avisos legales.'}
                  </p>
                )}
                {!siniestro && i.avisosAceptados && (
                  <details className="text-[13px] text-text-long">
                    <summary className="cursor-pointer font-bold text-primary-text">
                      Ver los avisos que aceptó
                    </summary>
                    {/* Its frozen copy (default text if signed before the copy existed). */}
                    <ol className="mt-2 flex list-decimal flex-col gap-1 pl-5">
                      {avisosFirmados(i).map((a, n) => (
                        <li key={n}>{a}</li>
                      ))}
                    </ol>
                  </details>
                )}
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t border-border pt-3 text-[13px]">
                  <Dato t="Registró">{autor}</Dato>
                  {i.dispositivoId && (
                    <Dato t="Tablet">
                      {dispositivos.data?.find((d) => d.id === i.dispositivoId)?.nombre ??
                        i.dispositivoId}
                    </Dato>
                  )}
                  <Dato t="Creada">{creada ? fechaCompleta(creada) : '—'}</Dato>
                  {i.firma?.en && <Dato t="Firmada">{fechaCompleta(new Date(i.firma.en))}</Dato>}
                  {siniestro && !i.firma && i.firmadaEn && (
                    <Dato t="Cerrada">{fechaCompleta(new Date(i.firmadaEn))}</Dato>
                  )}
                  {i.revision && (
                    <Dato t="Revisión">
                      <span
                        className={
                          i.revision.resultado === 'aprobada' ? 'text-sin-texto' : 'text-ray-texto'
                        }
                      >
                        {i.revision.resultado === 'aprobada' ? 'Aprobada' : 'Observada'}
                      </span>{' '}
                      por{' '}
                      {i.revision.porNombre ??
                        usuarios.data?.find((u) => u.id === i.revision?.por)?.nombre ??
                        '—'}
                      {revisadaEn ? ` · ${fechaHora(revisadaEn)}` : ''}
                    </Dato>
                  )}
                </dl>
              </div>
            </Tarjeta>
          </div>
        </div>
      </div>

      <Visor
        items={visor?.items ?? []}
        indice={visor?.i ?? null}
        onIndice={(n) => setVisor((v) => (v ? { ...v, i: n } : v))}
        patente={i.patente}
        onCerrar={() => setVisor(null)}
      />
      {firmada && (
        <>
          <DialogoRevision
            key={`rev-${revisar}`}
            inspeccion={i}
            nombre={nombreYo}
            abierto={revisar}
            onCambio={setRevisar}
          />
          <DialogoAnular
            key={`anu-${anular}`}
            inspeccion={i}
            nombre={nombreYo}
            abierto={anular}
            onCambio={setAnular}
          />
        </>
      )}
      {puedeEnviar && (
        <DialogoEnviarPdf
          key={`env-${enviar}`}
          i={i}
          abierto={enviar}
          onCambio={setEnviar}
          onDescargar={() => void bajarPdf()}
          descargando={bajando}
        />
      )}
    </>
  );
}

/** Insurance data of a siniestro and whether the car was already admitted. */
function Seguro({ i }: { i: InspeccionDoc }) {
  return (
    <Tarjeta className="p-5">
      <TituloTarjeta>Seguro</TituloTarjeta>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-[14px]">
        <Dato t="Compañía">{i.seguro?.compania || '—'}</Dato>
        <Dato t="N° de siniestro">
          <span className="tabular">{i.seguro?.numeroSiniestro || '—'}</span>
        </Dato>
        <Dato t="Liquidador">{i.seguro?.liquidador || '—'}</Dato>
      </dl>
      <div className="mt-4 border-t border-border pt-4 text-[14px]">
        {i.ingresoId ? (
          <Link
            to="/inspecciones/$inspeccionId"
            params={{ inspeccionId: i.ingresoId }}
            className="font-bold text-primary-text hover:underline"
          >
            Ya ingresó al taller: abrir el ingreso →
          </Link>
        ) : (
          <p className="text-text-2">
            {i.estado === 'firmada'
              ? 'Pendiente de ingreso: se crea desde la tablet cuando el auto llegue.'
              : 'El ingreso se crea desde la tablet después de cerrar esta inspección.'}
          </p>
        )}
      </div>
    </Tarjeta>
  );
}

/** Chain of custody at a glance: how many masters the server re-hashed and the signed PDF. */
function Custodia({
  total,
  verificadas,
  pendientes,
  malas,
  i,
  bajando,
  onPdf,
}: {
  total: number;
  verificadas: number;
  pendientes: number;
  malas: number;
  i: InspeccionDoc;
  bajando: boolean;
  onPdf: () => void;
}) {
  const pct = total ? Math.round((verificadas / total) * 100) : 0;
  return (
    <Tarjeta className="p-5">
      <TituloTarjeta
        accion={
          malas > 0 ? (
            <InsigniaHash estado="alterada" />
          ) : total > 0 && pendientes === 0 ? (
            <InsigniaHash estado="verificada" />
          ) : total > 0 ? (
            <InsigniaHash estado="pendiente" />
          ) : undefined
        }
      >
        Integridad de la evidencia
      </TituloTarjeta>
      {total === 0 ? (
        <p className="mt-2 text-[14px] text-text-2">Esta inspección no tiene fotos.</p>
      ) : (
        <>
          <div
            className="mt-3 flex h-2 overflow-hidden rounded-full bg-cupo-pista"
            role="img"
            aria-label={`${verificadas} de ${total} fotos verificadas`}
          >
            <span className="bg-sin-strong" style={{ width: `${(verificadas / total) * 100}%` }} />
            <span className="bg-que-strong" style={{ width: `${(malas / total) * 100}%` }} />
          </div>
          <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Cifra n={verificadas} t="Verificadas" clase="text-sin-texto" />
            <Cifra n={pendientes} t="Por verificar" clase="text-text" />
            <Cifra n={malas} t="Con problemas" clase={malas ? 'text-que-texto' : 'text-text'} />
          </dl>
          <p className="mt-3 text-[13px] leading-relaxed text-text-2">
            {pct === 100
              ? 'El servidor recalculó la huella SHA-256 de cada foto original y todas coinciden con las de la tablet.'
              : 'Cada foto original se compara con la huella SHA-256 calculada en la tablet al tomarla. Abre una foto para ver ambas huellas.'}
          </p>
        </>
      )}
      <div className="mt-4 flex items-center gap-3 border-t border-border pt-4">
        <FileText className="size-5 flex-none text-text-2" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-[14px]">
          <p className="font-bold">
            {i.tipo === 'siniestro'
              ? i.pdfPath
                ? `Informe de siniestro N° ${i.numeroCorrelativo ?? '—'}`
                : 'Informe de siniestro (PDF)'
              : i.pdfPath
                ? `Boleta N° ${i.numeroCorrelativo ?? '—'}`
                : 'Boleta PDF'}
          </p>
          <p className="text-[13px] text-text-2">
            {i.pdfPath
              ? i.tipo === 'siniestro'
                ? 'Para el liquidador: datos, seguro, daños, fotos selladas y sus huellas.'
                : 'Incluye la huella de cada foto al momento de la firma.'
              : i.estado === 'borrador'
                ? i.tipo === 'siniestro'
                  ? 'Se genera al cerrar la inspección.'
                  : 'Se genera cuando el cliente firma.'
                : i.validacion && !i.validacion.ok
                  ? 'No se generó: la firma fue rechazada.'
                  : 'Generándose en el servidor…'}
          </p>
        </div>
        {i.pdfPath && (
          <Boton tamano="sm" onClick={onPdf} cargando={bajando}>
            <Download /> Descargar
          </Boton>
        )}
      </div>
    </Tarjeta>
  );
}

function Cifra({ n, t, clase }: { n: number; t: string; clase: string }) {
  return (
    <div className="flex flex-col-reverse rounded-[10px] bg-surface-muted px-2 py-2">
      <dt className="text-[12px] text-text-2">{t}</dt>
      <dd className={cn('tabular text-[20px] leading-tight font-bold', clase)}>{n}</dd>
    </div>
  );
}

function Dato({ t, children }: { t: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-text-2">{t}</dt>
      <dd className="min-w-0 text-text">{children}</dd>
    </>
  );
}

function Comparacion({
  i,
  tipoReferencia,
  recepcion,
  cargando,
  verifs,
  verifsRecepcion,
  onAbrir,
}: {
  i: InspeccionDoc;
  tipoReferencia: 'recepcion' | 'siniestro';
  recepcion: InspeccionDoc | null;
  cargando: boolean;
  verifs: Map<string, VerificacionFoto>;
  verifsRecepcion: Map<string, VerificacionFoto>;
  onAbrir: (f: Foto, lista: Foto[], vs: Map<string, VerificacionFoto>) => void;
}) {
  const discrepancias = i.discrepancias ?? [];
  const deSiniestro = tipoReferencia === 'siniestro';
  const ref = deSiniestro ? 'siniestro' : 'ingreso';
  const tituloComp = deSiniestro ? 'Comparado con el siniestro' : 'Comparación con el ingreso';
  // An Ingreso's own photos are its evidence ("antes"); a delivery/re-entry
  // documents the change with "despues" photos.
  const fotoPosterior = (z: string) =>
    i.zonas?.[z]?.fotoIds?.find((f) => deSiniestro || i.fotos[f]?.momento === 'despues');
  const nuevas = discrepancias.filter((d) => d.tipo !== 'reparado');
  const conPar = discrepancias.filter((d) => {
    const antes = recepcion?.zonas?.[d.zona]?.fotoIds?.[0];
    const despues = fotoPosterior(d.zona);
    return antes && despues;
  });
  // The reception loads after the first render: default to the first zone
  // with a before/after pair once it is known, without overriding a choice.
  const [elegida, setZonaComp] = useState<string | null>(null);
  const zonaComp = elegida ?? conPar[0]?.zona ?? discrepancias[0]?.zona ?? null;
  const nombre = (z: string) => catalogo.zonas.find((x) => x.id === z)?.nombre ?? z;

  if (cargando) return <Esqueleto className="h-64 rounded-tarjeta" />;
  if (!recepcion) {
    return (
      <Tarjeta className="p-6">
        <TituloTarjeta>{tituloComp}</TituloTarjeta>
        <p className="mt-2 text-[14px] text-text-2">No se encontró el {ref} de referencia.</p>
      </Tarjeta>
    );
  }
  const antesF = zonaComp
    ? recepcion.fotos?.[recepcion.zonas?.[zonaComp]?.fotoIds?.[0] ?? '']
    : undefined;
  const despuesId = zonaComp ? fotoPosterior(zonaComp) : undefined;
  const despuesF = despuesId ? i.fotos[despuesId] : undefined;
  const estadosR = Object.fromEntries(
    Object.entries(recepcion.zonas ?? {}).map(([k, v]) => [k, v.estado]),
  );
  const estadosE = Object.fromEntries(Object.entries(i.zonas ?? {}).map(([k, v]) => [k, v.estado]));
  const resaltadas = new Set(discrepancias.map((d) => d.zona));

  return (
    <Tarjeta className="p-5 sm:p-6">
      <TituloTarjeta
        accion={
          <Link
            to="/inspecciones/$inspeccionId"
            params={{ inspeccionId: recepcion.id }}
            className="text-[13px] font-bold text-primary-text hover:underline"
          >
            Abrir {ref} N° {recepcion.numeroCorrelativo ?? '—'}
          </Link>
        }
      >
        {tituloComp}
      </TituloTarjeta>
      <div className="mt-4 grid gap-6 lg:grid-cols-[auto_minmax(0,1fr)]">
        <div className="flex justify-center gap-4">
          <figure className="flex flex-col items-center gap-1.5">
            <Gemelo
              estados={estadosR}
              ancho={110}
              resaltadas={resaltadas.size ? resaltadas : undefined}
              etiqueta={nombreTipo[tipoReferencia]}
            />
            <figcaption className="text-[12px] text-text-2">
              {nombreTipo[tipoReferencia]}
            </figcaption>
          </figure>
          <figure className="flex flex-col items-center gap-1.5">
            <Gemelo
              estados={estadosE}
              ancho={110}
              resaltadas={resaltadas.size ? resaltadas : undefined}
              etiqueta={nombreTipo[i.tipo]}
            />
            <figcaption className="text-[12px] text-text-2">{nombreTipo[i.tipo]}</figcaption>
          </figure>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          {sinComparacion(i) ? (
            <div className="rounded-[12px] border border-ray-strong/40 bg-ray-soft px-4 py-3 text-[14px] text-ray-texto">
              <b>Sin comparación:</b> el {ref} de referencia tiene datos dañados y el servidor no
              pudo comparar los daños. Revísalos a mano.
            </div>
          ) : discrepancias.length === 0 ? (
            <div className="rounded-[12px] border border-sin-strong/35 bg-sin-soft px-4 py-3 text-[14px] text-sin-texto">
              <b>Sin diferencias:</b>{' '}
              {deSiniestro
                ? 'el auto ingresó igual que en la inspección de siniestro.'
                : 'el auto está igual que en el ingreso.'}
            </div>
          ) : (
            <>
              <p className="text-[14px]">
                {nuevas.length > 0 ? (
                  <b className="text-que-texto">
                    {nuevas.length === 1
                      ? '1 zona con daño nuevo o peor'
                      : `${nuevas.length} zonas con daño nuevo o peor`}
                  </b>
                ) : (
                  <b className="text-sin-texto">Sin daños nuevos</b>
                )}
                <span className="text-text-2">
                  {' '}
                  · {discrepancias.length - nuevas.length} reparadas
                </span>
              </p>
              <ul className="flex flex-col gap-1.5">
                {discrepancias.map((d) => (
                  <li key={d.zona}>
                    <button
                      type="button"
                      onClick={() => setZonaComp(d.zona)}
                      aria-pressed={zonaComp === d.zona}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-[10px] border px-3 py-2 text-left text-[14px] transition-colors',
                        zonaComp === d.zona
                          ? 'border-primary-sel bg-primary-soft'
                          : 'border-border hover:bg-surface-muted',
                      )}
                    >
                      <span className="flex-1">{nombre(d.zona)}</span>
                      <span className="text-[13px] text-text-2">
                        {d.antes
                          ? catalogo.estadosZona.find((e) => e.id === d.antes)?.simbolo
                          : '–'}{' '}
                        →{' '}
                        {d.despues
                          ? catalogo.estadosZona.find((e) => e.id === d.despues)?.simbolo
                          : '–'}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[12px] font-bold',
                          d.tipo === 'reparado'
                            ? 'bg-sin-soft text-sin-texto'
                            : 'bg-que-soft text-que-texto',
                        )}
                      >
                        {d.tipo === 'reparado'
                          ? 'Reparado'
                          : d.tipo === 'empeora'
                            ? 'Empeoró'
                            : 'Daño nuevo'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
      {zonaComp && (antesF || despuesF) && (
        <div className="mt-5 border-t border-border pt-5">
          <p className="mb-3 text-[14px] font-bold">{nombre(zonaComp)}: antes y después</p>
          {antesF && despuesF ? (
            <ComparadorDeslizante
              antes={antesF}
              despues={despuesF}
              className="max-h-[520px] w-full"
            />
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {antesF && (
                <button
                  type="button"
                  onClick={() => onAbrir(antesF, [antesF], verifsRecepcion)}
                  className="text-left text-[13px] text-primary-text hover:underline"
                >
                  Ver foto del {ref}
                </button>
              )}
              {despuesF && (
                <button
                  type="button"
                  onClick={() => onAbrir(despuesF, [despuesF], verifs)}
                  className="text-left text-[13px] text-primary-text hover:underline"
                >
                  Ver foto {deSiniestro ? 'del ingreso' : `de la ${i.tipo}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </Tarjeta>
  );
}

function Cargando() {
  return (
    <>
      <div className="flex h-[76px] items-center border-b border-border bg-surface px-7">
        <Esqueleto className="h-7 w-72" />
      </div>
      <div
        className="mx-auto grid w-full max-w-[1320px] gap-5 px-7 py-6 xl:grid-cols-[minmax(0,1fr)_380px]"
        aria-busy="true"
      >
        <div className="flex flex-col gap-5">
          <Esqueleto className="h-[400px] rounded-tarjeta" />
          <Esqueleto className="h-64 rounded-tarjeta" />
        </div>
        <div className="flex flex-col gap-5">
          <Esqueleto className="h-56 rounded-tarjeta" />
          <Esqueleto className="h-40 rounded-tarjeta" />
        </div>
      </div>
    </>
  );
}
